import { useEffect, useRef, type RefObject } from 'react';

/** An element that edits text (a field, a contenteditable): Escape there belongs to the editing, not to a panel. */
export function isEditing(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.isContentEditable || target.matches('input, textarea, select'));
}

/**
 * Whether Escape should close a non-modal layer whose popup is `popup` (P16-2 Ruling 7): not when something already
 * handled it, not from a text surface outside the layer, not from another open layer (a menu or popover on top).
 */
export function escapeCloses(e: KeyboardEvent, popup: HTMLElement | null): boolean {
  if (e.key !== 'Escape' || e.defaultPrevented) return false;
  const target = e.target instanceof Element ? e.target : null;
  if (popup && target && popup.contains(target)) return true;
  if (isEditing(target)) return false;
  const layer = target?.closest('[role="menu"], [role="listbox"], [role="dialog"], [role="alertdialog"]');
  return !layer || layer === popup;
}

/**
 * Escape from anywhere closes a non-modal layer (Base UI only hears it from inside the popup). `close` is called once,
 * whichever way the layer closes; the returned function is that guarded close.
 */
export function useEscapeAnywhere(popup: RefObject<HTMLElement | null>, onClose: () => void): () => void {
  const closed = useRef(false);
  const latest = useRef(onClose);
  latest.current = onClose;
  const close = useRef(() => {
    if (closed.current) return;
    closed.current = true;
    latest.current();
  }).current;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => escapeCloses(e, popup.current) && close();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [popup, close]);
  return close;
}
